import React, { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { CheckOutlined } from '@ant-design/icons';
import {
    Catcher,
    LinkComponent,
    MetadataCollapse,
    ShowDataBytes,
    ShowVoice,
    TableItemToolbar,
    columnToolbar,
} from '../../';
import { ICallDataRecordsListItem, ITableInclude } from '../../../interfaces';
import {
    useCallDataRecordsListBeta,
    usePagesStoreThisListRight,
    useProfileSettings,
    useUiIsSpinnerFound,
} from '../../../hooks';
import { smsRange } from '../../../instruments';
import { tF, tI } from '../../../translate';
import Styles from '../../styles/m_viewStyles.less';

type IDataType = ICallDataRecordsListItem & {
    key: string;
    cardShortIdLink?: ReactNode;
    cardNameLink?: ReactNode;
    operatorNameLink?: ReactNode;
    countryLink?: ReactNode;
};

export const CallDataRecordsBetaTableList: React.FC<ITableInclude> = observer(({ isIncluded }: ITableInclude) => {
    const isLoading = useUiIsSpinnerFound(['GET_CALL_DATA_SQUASHED_RECORDS_LIST', 'GET_CALL_DATA_RECORDS_LIST']);
    const list = useCallDataRecordsListBeta();
    const right = usePagesStoreThisListRight();
    const { hideCosts } = useProfileSettings();

    const tableColumns: ColumnsType<IDataType> = [
        {
            title: tF('Card Name'),
            dataIndex: 'cardNameLink',
        },
        {
            title: tF('Card'),
            dataIndex: 'cardShortIdLink',
        },
        {
            title: tF('Operator'),
            dataIndex: 'operatorNameLink',
        },
        {
            title: tF('Country'),
            dataIndex: 'countryLink',
        },
        {
            title: tF('Usage'),
            dataIndex: 'showUnits',
            className: Styles.columnRight,
        },
        hideCosts
            ? {}
            : {
                  title: tF('Cost'),
                  dataIndex: 'cost',
                  className: Styles.columnRight,
              },
        {
            title: tF('Start Date'),
            dataIndex: 'startDate',
            width: 180,
            className: Styles.columnRight,
        },
        !isIncluded ? columnToolbar : {},
    ];

    const tableData: IDataType[] = list.map((item: ICallDataRecordsListItem) => {
        return {
            key: item.id,
            ...item,
            cardShortIdLink: (
                <LinkComponent currentListComponent={'CardList'} id={item.cardId} isLight name={item.cardShortId} />
            ),
            cardNameLink: (
                <LinkComponent currentListComponent={'CardList'} id={item.cardId} isLight name={item.cardName} />
            ),
            operatorNameLink: (
                <LinkComponent
                    currentListComponent={'OperatorsList'}
                    id={item.operatorId}
                    isLight
                    name={item.operatorName}
                />
            ),
            countryLink: (
                <LinkComponent
                    currentListComponent={'CountryList'}
                    id={item.countryId}
                    isLight
                    name={item.countryName}
                />
            ),
            showUnits:
                item.source === 'byte' ? (
                    <ShowDataBytes bytes={item?.units ? +item.units : undefined} isHumanView />
                ) : item.source === 'sms' ? (
                    smsRange(item?.units)
                ) : item.source === 'sec' ? (
                    <ShowVoice valueSec={item?.units} />
                ) : (
                    item?.units
                ),
            toolbar: <TableItemToolbar id={item.id} itemName={`"${item.id}" ${tI('Call Data Record')}`} {...right} />,
        };
    });

    const expandedRowRender = (record: ICallDataRecordsListItem): JSX.Element => {
        return (
            <div className={Styles.expandedRow}>
                <div className={Styles.expandedRowLine}>
                    <div className={Styles.expandedRowLineLabel}>{tF('Card Set')}</div>
                    <div className={Styles.expandedRowLineValue}>
                        <LinkComponent
                            currentListComponent={'CardSetsList'}
                            id={record.cardSetId}
                            isLight
                            name={record.cardSetName}
                        />
                    </div>
                </div>
                <div className={Styles.expandedRowLine}>
                    <div className={Styles.expandedRowLineLabel}>{tF('PLMN')}</div>
                    <div className={Styles.expandedRowLineValue}>
                        <LinkComponent
                            currentListComponent={'CardList'}
                            id={record.cardId}
                            isLight
                            name={record.cardPlmnList}
                        />
                    </div>
                </div>
                <div className={Styles.expandedRowLine}>
                    <div className={Styles.expandedRowLineLabel}>{tF('Operator')}</div>
                    <div className={Styles.expandedRowLineValue}>
                        <LinkComponent
                            currentListComponent={'OperatorsList'}
                            id={record.operatorId}
                            isLight
                            name={record.operatorName}
                        />
                    </div>
                </div>
                <div className={Styles.expandedRowLine}>
                    <div className={Styles.expandedRowLineLabel}>{tF('IMSI')}</div>
                    <div className={Styles.expandedRowLineValue}>
                        <LinkComponent
                            currentListComponent={'SubscribersList'}
                            id={record.subscriberId}
                            isLight
                            name={record.imsi}
                        />
                    </div>
                </div>
                <div className={Styles.expandedRowLine}>
                    <div className={Styles.expandedRowLineLabel}>{tF('Visited Network')}</div>
                    <div className={Styles.expandedRowLineValue}>
                        <LinkComponent
                            currentListComponent={'OperatorsList'}
                            id={record.visitedNetworkId}
                            isLight
                            name={record.visitedNetworkName}
                        />
                    </div>
                </div>
                <div className={Styles.expandedRowLine}>
                    <div className={Styles.expandedRowLineLabel}>{tF('Source')}</div>
                    <div className={Styles.expandedRowLineValue}>{record.source}</div>
                </div>
                <div className={Styles.expandedRowLine}>
                    <div className={Styles.expandedRowLineLabel}>{tF('End')}</div>
                    <div className={Styles.expandedRowLineValue}>{record.endDate}</div>
                </div>
                {record.billedAsPackage ? (
                    <div className={Styles.expandedRowLine}>
                        <div className={Styles.expandedRowLineLabel}>{tF('Billed as package')}</div>
                        <div className={Styles.expandedRowLineValue}>
                            <CheckOutlined />
                        </div>
                    </div>
                ) : null}
                <MetadataCollapse metadata={record.metadata} />
            </div>
        );
    };

    const expandable = { expandedRowRender };

    return (
        <Catcher>
            <div className={Styles.tablePlace}>
                <Table
                    className={'table-theme'}
                    columns={tableColumns}
                    dataSource={tableData}
                    expandable={expandable}
                    loading={isLoading}
                    pagination={false}
                    size={'small'}
                />
            </div>
        </Catcher>
    );
});
